<script setup>
import { computed, ref } from 'vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import FormattedContent from './FormattedContent.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';
import { MESSAGE_TYPES } from '../../constants';
import { useMessageContext } from '../../provider.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, attachments, contentAttributes, messageType } =
  useMessageContext();

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const renderOriginal = ref(false);

const renderContent = computed(() => {
  if (renderOriginal.value) {
    return content.value;
  }

  if (hasTranslations.value) {
    return translationContent.value;
  }

  return content.value;
});

const isTemplate = computed(() => {
  return messageType.value === MESSAGE_TYPES.TEMPLATE;
});

const isEmpty = computed(() => {
  return !content.value && !attachments.value?.length;
});

const hasVisibleText = computed(() => {
  if (isEmpty.value) return false;
  if (!renderContent.value) return false;
  if (typeof renderContent.value === 'string' && renderContent.value.trim() === '') return false;
  return true;
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};

const expanded = ref(false);
const isLongText = computed(() => {
  return renderContent.value && renderContent.value.length > 500;
});
</script>

<template>
  <BaseBubble :class="[hasVisibleText || isEmpty ? 'px-4 py-3' : 'p-1']" data-bubble-name="text">
    <div class="gap-3 flex flex-col">
      <span v-if="isEmpty" class="text-n-slate-11">
        {{ $t('CONVERSATION.NO_CONTENT') }}
      </span>
      <div
        v-if="hasVisibleText"
        class="relative transition-all duration-300"
        :class="{ 'line-clamp-[10] overflow-hidden': !expanded && isLongText }"
      >
        <FormattedContent :content="renderContent" />
        <div v-if="!expanded && isLongText" class="absolute bottom-0 left-0 w-full h-12 bg-gradient-to-t from-black/10 to-transparent pointer-events-none"></div>
      </div>
      <button
        v-if="!expanded && isLongText"
        class="text-left text-[#000000] underline text-[13px] font-bold tracking-wide hover:opacity-80 mt-[-6px]"
        @click="expanded = true"
      >
        Read more
      </button>
      <TranslationToggle
        v-if="hasTranslations"
        class="-mt-3"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <AttachmentChips :attachments="attachments" class="gap-2" />
      <template v-if="isTemplate">
        <div
          v-if="contentAttributes.submittedEmail"
          class="px-2 py-1 rounded-lg bg-n-alpha-3"
        >
          {{ contentAttributes.submittedEmail }}
        </div>
      </template>
    </div>
  </BaseBubble>
</template>

<style>
p:last-child {
  margin-bottom: 0;
}
</style>
